// SwitcherPopupHeader.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useBase } from 'context/BaseContext';

// Services
import {
    isBaseBillingInGracePeriod,
    isBaseBillingNavigationLocked,
} from 'services/billing/BaseBillingService';

// Utilities
import { formatBaseLockedNotice } from 'utilities/baseAccessCopy';

// Hooks
import useBaseAccessLock from 'hooks/useBaseAccessLock';

// CSS
import './SwitcherPopupHeader.css';

function formatBaseBillingNotice(base, record, lockReason) {

    if (lockReason === 'trial_expired') {
        return formatBaseLockedNotice();
    }

    if (!record) return '';

    const gracePeriodEndsAt = record?.gracePeriodEndsAt instanceof Date
        ? record.gracePeriodEndsAt
        : null;

    if (isBaseBillingNavigationLocked(record)) {
        return 'Billing access is locked. Open space settings to update payment and restore navigation.';
    }

    if (!isBaseBillingInGracePeriod(record)) {
        return '';
    }

    if (!gracePeriodEndsAt) {
        return 'Billing issue detected. Update payment to avoid navigation lock.';
    }

    return `Billing issue detected. Update payment before ${gracePeriodEndsAt.toLocaleString()}.`;
}


/**
 * SwitcherPopupHeader
 * 
 * Displays the header section of the switcher popup.
 *  
 *
 * @returns {ReactNode} - SwitcherPopupHeader.
 */
const SwitcherPopupHeader = React.memo(() => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // Base Context
    const { activeBase } = useBase();

    // ----- HOOKS ----- //

    const {
        accessLocked: baseNavigationLocked,
        billingRecord,
        lockReason,
    } = useBaseAccessLock();

    // ----- MEMOS ----- //

    const billingNotice = useMemo(() => {
        return formatBaseBillingNotice(activeBase, billingRecord, lockReason);
    }, [activeBase, billingRecord, lockReason]);

    const basePlanTitle = String(activeBase?.planTitle || '').trim();

    // ----- RENDER ----- //

    return (

        <div
          className="switcher-popup-header-container"
        >

            {/* TOP */}
            <div className="switcher-popup-header-top">

                {/* HEADER RIGHT */}
                <div className="switcher-popup-header-right">

                    {/* TITLE TEXT */}
                    <div
                      className="switcher-popup-title-text"
                    >
                        {activeBase?.title || ''}
                    </div>

                    {/* TITLE FOOTER */}
                    <div
                      className="switcher-popup-subtitle"
                    >
                        {basePlanTitle}
                    </div>

                    {/* BILLING ALERT */}
                    {billingNotice && (
                        <div
                          className="switcher-popup-billing-alert"
                          style={{
                            backgroundColor: baseNavigationLocked
                                    ? (theme.warning || theme.backgroundHover)
                                    : theme.backgroundHover,
                            borderColor: baseNavigationLocked
                                    ? (theme.warning || theme.backgroundHover)
                                    : theme.backgroundHover,
                          }}
                        >
                            {billingNotice}
                        </div>
                    )}

                </div>

            </div>

        </div>
    );

});

// Dev Tools
SwitcherPopupHeader.displayName = 'SwitcherPopupHeader';

export default SwitcherPopupHeader;
